import Phaser from 'phaser';
import { AudioFileKey } from '@/config/audioPacks';
import { loadSettings } from '@/config/settings';

let sharedAudioManager: AudioManager | null = null;

export class AudioManager {
  private scene: Phaser.Scene;
  private sfxVolume: number;
  private musicVolume: number;
  private currentMusic: Phaser.Sound.BaseSound | null = null;

  // Web Audio API for continuous morse tone
  private audioContext: AudioContext | null = null;
  private oscillator: OscillatorNode | null = null;
  private gainNode: GainNode | null = null;
  private readonly TONE_FREQUENCY = 600;  // Classic morse tone frequency (Hz)

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const settings = loadSettings();
    this.sfxVolume = settings.sfxVolume;
    this.musicVolume = settings.musicVolume;
  }

  /**
   * Get the shared audio manager (persists across scenes).
   */
  static get(scene: Phaser.Scene): AudioManager {
    if (!sharedAudioManager) {
      sharedAudioManager = new AudioManager(scene);
    } else {
      sharedAudioManager.scene = scene;
    }
    return sharedAudioManager;
  }

  /**
   * Start continuous morse tone (called on keydown)
   */
  startTone(): void {
    // Don't start if already playing
    if (this.oscillator) return;

    // Create audio context on first use (must be after user interaction)
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    // Resume context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    // Create oscillator (sine wave)
    this.oscillator = this.audioContext.createOscillator();
    this.oscillator.type = 'sine';
    this.oscillator.frequency.value = this.TONE_FREQUENCY;

    // Create gain node for volume control
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = this.sfxVolume * 0.3;  // Scale down to not be too loud

    // Connect: oscillator -> gain -> output
    this.oscillator.connect(this.gainNode);
    this.gainNode.connect(this.audioContext.destination);

    // Start the tone
    this.oscillator.start();
  }

  /**
   * Stop continuous morse tone (called on keyup)
   */
  stopTone(): void {
    if (this.oscillator) {
      this.oscillator.stop();
      this.oscillator.disconnect();
      this.oscillator = null;
    }
    if (this.gainNode) {
      this.gainNode.disconnect();
      this.gainNode = null;
    }
  }

  /**
   * Start background music
   */
  playMusic(key: string): void {
    if (this.currentMusic) {
      this.currentMusic.stop();
    }

    if (this.scene.cache.audio.exists(key)) {
      this.currentMusic = this.scene.sound.add(key, {
        volume: this.musicVolume,
        loop: true,
      });
      this.currentMusic.play();
    }
  }

  /**
   * Stop background music
   */
  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic = null;
    }
  }

  playSfx(key: AudioFileKey, config?: Phaser.Types.Sound.SoundConfig): void {
    if (!this.scene.cache.audio.exists(key)) return;
    const volume = typeof config?.volume === 'number' ? config.volume : this.sfxVolume;
    this.scene.sound.play(key, { ...config, volume });
  }

  /**
   * Update volume settings
   */
  updateVolume(sfx?: number, music?: number): void {
    if (sfx !== undefined) {
      this.sfxVolume = sfx;
    }
    if (music !== undefined) {
      this.musicVolume = music;
      if (this.currentMusic && 'setVolume' in this.currentMusic) {
        (this.currentMusic as Phaser.Sound.WebAudioSound).setVolume(this.musicVolume);
      }
    }
  }

  /**
   * Mute all sounds
   */
  mute(): void {
    this.scene.sound.mute = true;
  }

  /**
   * Unmute all sounds
   */
  unmute(): void {
    this.scene.sound.mute = false;
  }

  /**
   * Toggle mute
   */
  toggleMute(): boolean {
    this.scene.sound.mute = !this.scene.sound.mute;
    return this.scene.sound.mute;
  }

  /**
   * Check if audio is available
   */
  isAudioAvailable(): boolean {
    return this.scene.sound.locked === false;
  }

  /**
   * Clean up
   */
  destroy(): void {
    this.stopTone();
    this.stopMusic();

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (sharedAudioManager === this) {
      sharedAudioManager = null;
    }
  }
}
